import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, Sparkles, Undo2 } from 'lucide-react';
import { isAnimatedScene, type EditorClip } from '@rehub/propertycut-timeline';
import type { PropertyCutAsset } from '../../types';
import { IMAGEMOTION_LOGO_SRC, VIDEOMOTION_LOGO_SRC } from '../../constants';
import { formatAspect, isActiveJob } from '../../services/jobs';
import { cropStyle } from './model';
import type { SceneTools } from './useSceneJobs';

function credits(n: number): string {
  return n === 1 ? '1 credit' : `${n} credits`;
}

export function PoweredByImageMotion() {
  return (
    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
      Powered by
      <img src={IMAGEMOTION_LOGO_SRC} alt="ImageMotion" className="h-3 w-auto opacity-80" />
    </p>
  );
}

function ConfirmRow({ label, onConfirm, onCancel }: { label: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onConfirm}
        className="flex-1 rounded-md bg-teal-400 text-slate-950 text-xs font-semibold py-2"
      >
        {label}
      </button>
      <button type="button" onClick={onCancel} className="rounded-md border border-slate-700 px-3 text-xs text-slate-300">
        Cancel
      </button>
    </div>
  );
}

/** Shows the exact frame ImageMotion will animate: the photo cropped to the video's shape. */
function FramingPreview({ url, clip, tools }: { url: string; clip: EditorClip; tools: SceneTools }) {
  const aspect = formatAspect(tools.format);
  const framing = clip.sourceAssetId && clip.animation?.still ? clip.animation.still : clip;
  return (
    <div
      className="relative w-full overflow-hidden rounded-md bg-slate-800"
      style={{ aspectRatio: `${aspect.width} / ${aspect.height}`, maxHeight: 220, marginInline: 'auto' }}
    >
      <img
        src={url}
        alt="Framing for the animation"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: cropStyle(framing.offsetX, framing.offsetY) }}
      />
    </div>
  );
}

export function SceneMotionPanel({
  clip,
  photo,
  tools,
}: {
  clip: EditorClip;
  photo: PropertyCutAsset;
  tools: SceneTools;
}) {
  const [confirming, setConfirming] = useState(false);
  const job = tools.latestSceneJob(clip.id);
  const animated = isAnimatedScene(clip);
  const generatedId = clip.animation?.assetId ?? null;
  const generated = generatedId ? tools.assetById(generatedId) : null;
  const running = Boolean(tools.pending[clip.id]) || Boolean(job && isActiveJob(job) && clip.animation?.jobId === job.id);
  const error = tools.errors[clip.id] ?? (job?.status === 'failed' && !generated ? job.error_message : null);
  const price = tools.quote?.scene.credits ?? null;
  const unavailable = tools.quoteError ?? tools.quote?.scene.reason ?? (price === null ? 'Loading price…' : null);
  const short = price !== null && tools.quote !== null && tools.quote.balance < price;

  useEffect(() => setConfirming(false), [clip.id]);

  const askLabel = generated ? 'Make a new animation' : 'Animate with ImageMotion';

  return (
    <div className="space-y-2.5 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium text-slate-200">
          <Sparkles className="h-3.5 w-3.5 text-teal-300" />
          {animated ? 'Animated scene' : generated ? 'Animation ready' : 'Animate this photo'}
        </p>
        <PoweredByImageMotion />
      </div>

      {generated?.signed_url && (
        <video
          key={generated.id}
          src={generated.signed_url}
          muted
          loop
          autoPlay
          playsInline
          className="w-full rounded-md bg-black"
          style={{ maxHeight: 220 }}
        />
      )}

      {generated && !animated && !running && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => tools.onUseAnimation(clip.id)}
            className="rounded-md bg-teal-400 text-slate-950 text-xs font-semibold py-2"
          >
            Use animation
          </button>
          <span className="rounded-md border border-slate-700 text-xs text-slate-300 py-2 text-center">Photo in use</span>
        </div>
      )}
      {animated && !running && (
        <button
          type="button"
          onClick={() => tools.onRevertAnimation(clip.id)}
          className="w-full flex items-center justify-center gap-1.5 rounded-md border border-slate-700 py-2 text-xs text-slate-200"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Back to the photo
        </button>
      )}
      {generated && (
        <p className="text-[11px] text-slate-500">
          The animation stays saved if you go back to the photo. Switching back does not refund credits.
        </p>
      )}

      {running && (
        <p className="flex items-center gap-2 text-xs text-teal-100">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {job?.status === 'queued' || tools.pending[clip.id] ? 'Starting the animation…' : 'Animating… this usually takes a few minutes. You can keep editing.'}
        </p>
      )}

      {!running && !animated && !generated && photo.signed_url && (
        <>
          <FramingPreview url={photo.signed_url} clip={clip} tools={tools} />
          <p className="text-[11px] text-slate-500">
            {clip.fit === 'contain'
              ? 'Animations fill the frame, so ImageMotion uses this crop. Move it with Crop position.'
              : 'ImageMotion animates this crop. Move it with Crop position.'}
          </p>
        </>
      )}

      {error && !running && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          {error}
        </p>
      )}

      {!running && (
        <>
          {confirming && price !== null ? (
            <ConfirmRow
              label={`Confirm · ${credits(price)}`}
              onConfirm={() => {
                setConfirming(false);
                void tools.onAnimate(clip.id);
              }}
              onCancel={() => setConfirming(false)}
            />
          ) : (
            <button
              type="button"
              disabled={Boolean(unavailable) || short}
              onClick={() => setConfirming(true)}
              className={`w-full rounded-md py-2 text-xs font-semibold disabled:opacity-40 ${
                generated ? 'border border-slate-700 text-slate-200' : 'bg-teal-400 text-slate-950'
              }`}
            >
              {askLabel}
            </button>
          )}
          <p className="text-[11px] text-slate-400">
            {unavailable
              ? unavailable
              : short
                ? `${credits(price!)} · 5-second animation. You have ${credits(tools.quote!.balance)}.`
                : `${credits(price!)} · ${tools.quote!.scene.seconds}-second animation`}
          </p>
        </>
      )}
    </div>
  );
}

export function AnimateAllPhotos({ tools }: { tools: SceneTools }) {
  const [confirming, setConfirming] = useState(false);
  const count = tools.eligiblePhotos.length;
  const price = tools.quote?.scene.credits ?? null;
  const { batch } = tools;
  if (count === 0 && !batch.running && !batch.summary) return null;
  const total = price !== null ? price * count : null;
  const balance = tools.quote?.balance ?? 0;
  const short = total !== null && balance < total;
  const unavailable = tools.quoteError ?? tools.quote?.scene.reason ?? null;

  return (
    <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-slate-200">Animate all photos</p>
        <PoweredByImageMotion />
      </div>
      {batch.running ? (
        <p className="flex items-center gap-2 text-xs text-teal-100">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Starting {Math.min(batch.done + 1, batch.total)} of {batch.total}…
        </p>
      ) : count > 0 && total !== null && !unavailable ? (
        confirming ? (
          <>
            <p className="text-[11px] text-slate-300">
              Animate {count} {count === 1 ? 'photo' : 'photos'} for {credits(total)} ({credits(price!)} each, 5 seconds each).
              Photos you already animated are not included.
            </p>
            <ConfirmRow
              label={`Animate ${count} · ${credits(total)}`}
              onConfirm={() => {
                setConfirming(false);
                void tools.onAnimateAll();
              }}
              onCancel={() => setConfirming(false)}
            />
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={short}
              onClick={() => setConfirming(true)}
              className="w-full rounded-md border border-teal-400/60 py-2 text-xs text-teal-100 disabled:opacity-40"
            >
              Animate {count} {count === 1 ? 'photo' : 'photos'} · {credits(total)}
            </button>
            {short && <p className="text-[11px] text-amber-200">You have {credits(balance)}. Animate photos one at a time, or buy more credits.</p>}
          </>
        )
      ) : (
        unavailable && <p className="text-[11px] text-slate-500">{unavailable}</p>
      )}
      {batch.summary && !batch.running && <p className="text-[11px] text-slate-300">{batch.summary}</p>}
    </div>
  );
}

const FILMING_TIPS = [
  'Film in the order the video should play: exterior approach first, then a natural route through the rooms.',
  `Record in the same shape as this video. Clips in the other orientation are cropped or fitted with a background.`,
  'MP4 or MOV, up to 5 minutes per clip.',
  'VideoMotion keeps every frame and only changes speed: it slows down for key rooms and speeds through hallways and transitions.',
];

export function WalkthroughCard({ tools, defaultOpen }: { tools: SceneTools; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [confirming, setConfirming] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const job = tools.walkthroughJob;
  const price = tools.quote?.walkthrough.credits ?? null;
  const max = tools.quote?.walkthrough.maxClips ?? 10;
  const maxSeconds = tools.quote?.walkthrough.maxClipSeconds ?? 300;
  const chosen = tools.walkthroughClips
    .filter((item) => !excluded.has(item.asset.id))
    .slice(0, max);
  const tooLong = chosen.filter((item) => (item.asset.duration_seconds ?? 0) > maxSeconds);
  const running = tools.walkthroughBusy || Boolean(job && isActiveJob(job));
  const short = price !== null && tools.quote !== null && tools.quote.balance < price;
  const blocked = tools.quoteError ?? (price === null ? 'Loading price…' : null);

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center justify-between gap-2 p-2.5"
      >
        <span className="flex items-center gap-2 text-xs font-medium text-slate-200">
          <img src={VIDEOMOTION_LOGO_SRC} alt="" className="h-3.5 w-auto" />
          VideoMotion walkthrough
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-2.5 px-2.5 pb-2.5">
          <ul className="space-y-1 text-[11px] text-slate-400 list-disc pl-4">
            {FILMING_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
          </ul>
          <p className="text-[11px] text-slate-500">
            Upload your clips, put them in order on the timeline and trim them there. Photos and animations can sit between clips.
          </p>

          {running && (
            <p className="flex items-center gap-2 text-xs text-teal-100">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {tools.walkthroughBusy ? 'Sending clips to VideoMotion…' : 'VideoMotion is processing your clips. This can take several minutes; you can keep editing.'}
            </p>
          )}
          {!running && job?.status === 'failed' && (
            <p className="flex items-start gap-1.5 text-[11px] text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
              {job.error_message || 'VideoMotion processing failed.'}
            </p>
          )}
          {!running && job?.status === 'ready' && tools.walkthroughUnapplied > 0 && (
            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs text-teal-100">
                <CheckCircle2 className="h-3.5 w-3.5" />
                VideoMotion clips are ready.
              </p>
              <button
                type="button"
                onClick={tools.onApplyWalkthrough}
                className="w-full rounded-md bg-teal-400 text-slate-950 text-xs font-semibold py-2"
              >
                Use VideoMotion clips on the timeline
              </button>
            </div>
          )}
          {tools.walkthroughError && <p className="text-[11px] text-amber-200">{tools.walkthroughError}</p>}

          {!running && (
            tools.walkthroughClips.length === 0 ? (
              <p className="text-[11px] text-slate-500">Add walkthrough clips to the timeline to process them with VideoMotion.</p>
            ) : (
              <>
                <ul className="space-y-1">
                  {tools.walkthroughClips.map((item, index) => {
                    const included = chosen.some((pick) => pick.asset.id === item.asset.id);
                    return (
                      <li key={item.asset.id}>
                        <label className="flex items-center gap-2 text-[11px] text-slate-300">
                          <input
                            type="checkbox"
                            checked={included}
                            disabled={!included && chosen.length >= max}
                            onChange={(event) => setExcluded((current) => {
                              const next = new Set(current);
                              if (event.target.checked) next.delete(item.asset.id);
                              else next.add(item.asset.id);
                              return next;
                            })}
                          />
                          <span className="tabular-nums text-slate-500">{index + 1}.</span>
                          <span className="truncate">{item.asset.original_filename || 'Clip'}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {tooLong.length > 0 && <p className="text-[11px] text-amber-200">Clips must be 5 minutes or shorter.</p>}
                {confirming && price !== null ? (
                  <ConfirmRow
                    label={`Confirm · ${credits(price)}`}
                    onConfirm={() => {
                      setConfirming(false);
                      void tools.onStartWalkthrough(chosen.map((item) => item.asset.id));
                    }}
                    onCancel={() => setConfirming(false)}
                  />
                ) : (
                  <button
                    type="button"
                    disabled={chosen.length === 0 || tooLong.length > 0 || short || Boolean(blocked)}
                    onClick={() => setConfirming(true)}
                    className="w-full rounded-md bg-teal-400 text-slate-950 text-xs font-semibold py-2 disabled:opacity-40"
                  >
                    Apply VideoMotion to {chosen.length} {chosen.length === 1 ? 'clip' : 'clips'}
                  </button>
                )}
                <p className="text-[11px] text-slate-400">
                  {blocked
                    ? blocked
                    : `${credits(price!)} per run of up to ${max} clips, the same as a finished VideoMotion video.${
                      short ? ` You have ${credits(tools.quote!.balance)}.` : ''
                    }`}
                </p>
              </>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function FootageVersionSwitch({ clip, tools }: { clip: EditorClip; tools: SceneTools }) {
  if (clip.videoMotion) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
        <span className="flex items-center gap-1.5 text-[11px] text-slate-300">
          <img src={VIDEOMOTION_LOGO_SRC} alt="" className="h-3 w-auto" />
          VideoMotion version
        </span>
        <button type="button" onClick={() => tools.onUseOriginal(clip.id)} className="text-[11px] text-teal-200">
          Use original footage
        </button>
      </div>
    );
  }
  if (!tools.processedFor(clip.assetId)) return null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2.5">
      <span className="text-[11px] text-slate-300">Original footage</span>
      <button type="button" onClick={() => tools.onUseProcessed(clip.id)} className="text-[11px] text-teal-200">
        Use VideoMotion version
      </button>
    </div>
  );
}
